import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'warn';

interface ToastState {
  message: string;
  type: ToastType;
  id: number;
}

interface ToastContextValue {
  success: (message: string | undefined) => void;
  error: (message: string | undefined) => void;
  warn: (message: string | undefined) => void;
  clear: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS: Record<ToastType, number> = {
  success: 5000,
  error: 8000,
  warn: 6000,
};

let toastIdCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const addToast = useCallback((message: string | undefined, type: ToastType) => {
    if (!message) return;
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { message, type, id }]);

    const timer = setTimeout(() => {
      dismissToast(id);
    }, AUTO_DISMISS_MS[type]);

    timersRef.current.set(id, timer);
  }, [dismissToast]);

  const success = useCallback((message: string | undefined) => addToast(message, 'success'), [addToast]);
  const error = useCallback((message: string | undefined) => addToast(message, 'error'), [addToast]);
  const warn = useCallback((message: string | undefined) => addToast(message, 'warn'), [addToast]);

  const clear = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const contextValue: ToastContextValue = { success, error, warn, clear };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {/* Toast UI */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-md">
        {toasts.map((toast) => (
          <button
            key={toast.id}
            type="button"
            onClick={() => dismissToast(toast.id)}
            className={getToastClassName(toast.type)}
          >
            {toast.message}
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function getToastClassName(type: ToastType): string {
  const base =
    'animate-in slide-in-from-top-2 fade-in duration-300 rounded-lg border px-4 py-3 text-sm shadow-lg backdrop-blur cursor-pointer text-left';

  switch (type) {
    case 'success':
      return `${base} border-emerald-500/40 bg-emerald-950/90 text-emerald-200`;
    case 'error':
      return `${base} border-destructive/40 bg-red-950/90 text-red-200`;
    case 'warn':
      return `${base} border-yellow-500/40 bg-yellow-950/90 text-yellow-200`;
  }
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
